// VISCA command helper functions.

// Limits are the maximum and minimum values for ptz commands sent by the joystick.
const Limits = {
  panTiltSpeedMax: 24,
  zoomSpeedMax: 7,
  zoomValueMax: 16383,
  panValueMin: -2400,
  panValueMax: 2400,
  tiltValueMin: -370,
  tiltValueMax: 1120
};

// Generic normalization logic
function normalizeSpeed(
  speed?: number,
  direction?: number,
  max?: number,
  negativeDir?: number,
  zeroDir?: number
): number | undefined {
  if (speed === undefined || direction === undefined || max === undefined) {
    return 0;
  }
  if (negativeDir && direction === negativeDir) {
    speed = -speed;
  }
  if (zeroDir !== undefined && direction === zeroDir) {
    speed = 0;
  }
  return speed / max;
}

export const normSpeed = {
  pan: (speed?: number, direction?: number) =>
    normalizeSpeed(speed, direction, Limits.panTiltSpeedMax, 1, 3),
  tilt: (speed?: number, direction?: number) =>
    normalizeSpeed(speed, direction, Limits.panTiltSpeedMax, 2, 3),
  zoom: (speed?: number, direction?: number) =>
    normalizeSpeed(speed, direction, Limits.zoomSpeedMax, 3),
  focus: (speed?: number, direction?: number) =>
    normalizeSpeed(speed, direction, Limits.zoomSpeedMax, 3)
};

// Normalizes a value to a range
type Range = {
  min: number;
  max: number;
};

// Limit a value to a range
function limit(range: Range, val: number): number {
  return Math.max(range.min, Math.min(range.max, val));
}

// Check if a range is valid
function isValid(range: Range): boolean {
  return range.min < range.max;
}

// Normalize a value to a range
export function rangeToRange(src: Range, dst: Range, val: number): number {
  if (!isValid(src) || !isValid(dst)) return 0;

  const res = ((val - src.min) / (src.max - src.min)) * (dst.max - dst.min) + dst.min;
  return limit(dst, res);
}

/**
 * Builds a VISCA command by replacing parameter placeholders with actual values.
 *
 * @param cmdStr - The VISCA command string where parameters are represented by lowercase letters
 *                 Example: "81 01 06 0v 0v 0w 0w 0w 0w FF"
 * @param params - Array of parameter values to insert into the command
 * @returns A Uint8Array containing the complete command
 */
export function buildCommand(cmdStr: string, params: number[]): Uint8Array {
  const VISCA_ERROR_PREFIX = 'VISCA: invalid command format';

  // Normalize to lowercase for consistent processing
  const lowerCmdStr = cmdStr.toLowerCase();

  // Initial validation of the command string parts (each part should be 2 chars long)
  const bytesStrOriginal = lowerCmdStr.split(' ');
  for (const bytePart of bytesStrOriginal) {
    if (bytePart.length !== 2) {
      throw new Error(
        `${VISCA_ERROR_PREFIX} - Each command part must be 2 characters long. Found: "${bytePart}"`
      );
    }
    // Further validation could be done here to check if chars are [0-9a-z],
    // but the main logic will handle specific hex vs placeholder chars.
  }

  // Remove spaces to get a continuous hex/placeholder string
  let workingCmdStr = lowerCmdStr.replace(/\s+/g, '');

  let currentParamIdx = 0; // Index for the params array
  const processedPlaceholderLetters = new Set<string>(); // Tracks letters like 'p', 'q' already processed

  // Loop as long as there are params to process
  while (currentParamIdx < params.length) {
    let currentPlaceholderLetter = '';

    // Find the *next* unprocessed placeholder letter (g-z) based on its earliest appearance in the string.
    // This ensures parameters are processed in the order their placeholders appear.
    for (let i = 0; i < workingCmdStr.length; i++) {
      const char = workingCmdStr[i]; // char is string | undefined with noUncheckedIndexedAccess
      // Ensure char is a defined string before using it
      if (char && char >= 'g' && char <= 'z') {
        if (!processedPlaceholderLetters.has(char)) {
          // This is a new, unprocessed placeholder type that appears earliest.
          currentPlaceholderLetter = char; // char is now confirmed string
          break;
        }
      }
    }

    if (!currentPlaceholderLetter) {
      // No more unprocessed g-z letters found in the string.
      // If there are still params left, it's an error.
      if (currentParamIdx < params.length) {
        throw new Error(
          `${VISCA_ERROR_PREFIX} - Not enough unique placeholder types (g-z) in command string for the given parameters. Processed ${currentParamIdx} parameters. Command string state: "${workingCmdStr}"`
        );
      }
      // All params processed, and no more placeholders to find.
      break;
    }

    // Count how many times this placeholder letter appears (e.g., 'v' in "0v0v" means 2 digits)
    let numDigitsForParam = 0;
    for (let i = 0; i < workingCmdStr.length; i++) {
      const char = workingCmdStr[i]; // string | undefined
      if (char === currentPlaceholderLetter) {
        // currentPlaceholderLetter is a confirmed string here
        numDigitsForParam++;
      }
    }

    if (numDigitsForParam === 0) {
      // This should technically not be reached if currentPlaceholderLetter was set and is valid.
      throw new Error(
        `${VISCA_ERROR_PREFIX} - Placeholder letter '${currentPlaceholderLetter}' was identified but has zero occurrences.`
      );
    }

    // Mark this letter as processed so it's not picked up again for a *new* parameter.
    processedPlaceholderLetters.add(currentPlaceholderLetter);

    const paramValue = params[currentParamIdx]; // number | undefined
    if (typeof paramValue !== 'number' || isNaN(paramValue)) {
      throw new Error(
        `${VISCA_ERROR_PREFIX} - Invalid or missing parameter value at index ${currentParamIdx}: ${paramValue}`
      );
    }

    // Convert the parameter value to a hex string, padded/sliced to the required number of digits.
    let paramHex = paramValue.toString(16);
    if (paramHex.length < numDigitsForParam) {
      paramHex = paramHex.padStart(numDigitsForParam, '0');
    } else if (paramHex.length > numDigitsForParam) {
      // If param value is too large (e.g., 0xfff for a 2-digit placeholder 'vv'),
      // take the lower significant digits, similar to how integer overflow might behave.
      paramHex = paramHex.slice(-numDigitsForParam);
    }

    // Replace all occurrences of the currentPlaceholderLetter with the digits from paramHex.
    let hexDigitForParamIdx = 0;
    const cmdChars = workingCmdStr.split(''); // string[]
    for (let i = 0; i < cmdChars.length; i++) {
      const charAtIndex = cmdChars[i]; // string | undefined

      if (charAtIndex === undefined) {
        // This should not happen given loop bounds i < cmdChars.length
        // and workingCmdStr being a result of string operations.
        throw new Error(
          `${VISCA_ERROR_PREFIX} - Unexpected undefined character in command string partway through processing.`
        );
      }

      if (charAtIndex === currentPlaceholderLetter) {
        if (hexDigitForParamIdx < paramHex.length) {
          const replacementDigit = paramHex[hexDigitForParamIdx]; // string | undefined
          if (replacementDigit === undefined) {
            // This implies an issue with paramHex or hexDigitForParamIdx logic
            throw new Error(
              `${VISCA_ERROR_PREFIX} - Logic error: undefined replacement digit from paramHex for '${currentPlaceholderLetter}'.`
            );
          }
          cmdChars[i] = replacementDigit; // Assigning string to string[] element
          hexDigitForParamIdx++;
        } else {
          // This indicates an issue, e.g. more placeholders than generated hex digits.
          // Should be caught by paramHex length logic, but as a safeguard:
          throw new Error(
            `${VISCA_ERROR_PREFIX} - Mismatch while replacing '${currentPlaceholderLetter}'. Expected ${numDigitsForParam} digits from param '${paramHex}', but ran out of digits.`
          );
        }
      }
    }
    workingCmdStr = cmdChars.join('');
    currentParamIdx++;
  }

  // After the loop, check if all params were used.
  if (currentParamIdx < params.length) {
    throw new Error(
      `${VISCA_ERROR_PREFIX} - Too many parameters provided for the placeholders in the command string. Processed ${currentParamIdx} of ${params.length} parameters.`
    );
  }

  // Final check: ensure no g-z letters remain (which would mean they weren't processed).
  // Also check for any other non-hex characters.
  for (let i = 0; i < workingCmdStr.length; i++) {
    const char = workingCmdStr[i]; // string | undefined
    if (char === undefined) {
      // This should not happen given loop bounds i < workingCmdStr.length
      throw new Error(
        `${VISCA_ERROR_PREFIX} - Unexpected undefined character in final command string before hex conversion.`
      );
    }
    if (!((char >= '0' && char <= '9') || (char >= 'a' && char <= 'f'))) {
      throw new Error(
        `${VISCA_ERROR_PREFIX} - Non-hexadecimal character '${char}' found in final command string: "${workingCmdStr}"`
      );
    }
  }

  // The final command string must have an even number of characters to form bytes.
  if (workingCmdStr.length % 2 !== 0) {
    throw new Error(
      `${VISCA_ERROR_PREFIX} - Final hex string has an odd length: ${workingCmdStr.length}. String: "${workingCmdStr}"`
    );
  }

  // Convert the final hex string to a Uint8Array.
  try {
    const resultBytes = new Uint8Array(workingCmdStr.length / 2);
    for (let i = 0; i < workingCmdStr.length; i += 2) {
      const byteHex = workingCmdStr.substring(i, i + 2);
      const byteValue = parseInt(byteHex, 16);
      if (isNaN(byteValue)) {
        // Should be caught by the earlier non-hex check
        throw new Error(
          `${VISCA_ERROR_PREFIX} - Invalid hex byte encountered: '${byteHex}' in final string.`
        );
      }
      resultBytes[i / 2] = byteValue;
    }
    return resultBytes;
  } catch (e: any) {
    // Catch any unexpected error during final conversion.
    throw new Error(
      `${VISCA_ERROR_PREFIX} - Failed to convert final hex string to bytes. ${e.message}`
    );
  }
}

/**
 * Extracts parameter values from a VISCA response packet based on a command template.
 *
 * @param cmdStr - The VISCA command template where parameters are represented by lowercase letters
 *                 Example: "81 01 06 0v 0v 0w 0w 0w 0w FF"
 * @param cmd - The actual command bytes received as a Uint8Array
 * @returns An array of extracted parameter values
 */
export function getParamValue(cmdStr: string, cmd: Uint8Array): number[] {
  const PARSER_ERROR = 'parser: invalid command format';

  // Normalize to lowercase
  cmdStr = cmdStr.toLowerCase();

  // Validate format - each part must be 2 chars (hex digit or letter)
  const bytesStr = cmdStr.split(' ');
  for (const c of bytesStr) {
    if (c.length !== 2 || !/^[0-9a-z]{2}$/.test(c)) {
      throw new Error(`${PARSER_ERROR}. Invalid character in command ${c}`);
    }
  }

  // Remove spaces
  const str = cmdStr.replace(/\s+/g, '');

  // Convert command bytes to hex string
  const s = Array.from(cmd)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  // Check if lengths match
  if (str.length !== cmd.length * 2) {
    throw new Error(
      `parser: command does not match template. Expected ${cmdStr}, got ${Array.from(cmd)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ')}`
    );
  }

  const values: number[] = [];
  let currentLetter = '';
  let currentValue = 0;

  // Process each character in the command string
  for (let i = 0; i < str.length; i++) {
    const c = str.charAt(i);

    // Check if current character is a parameter placeholder (lowercase letter that's not a hex digit)
    if (/[a-z]/.test(c) && !/[0-9a-f]/.test(c)) {
      if (!(currentLetter === '' || currentLetter === c)) {
        // Another letter is found, save the current value
        values.push(currentValue);
        currentValue = 0;
      }

      const byteIndex = Math.floor(i / 2);

      // Skip if the index is out of bounds (shouldn't happen with length check above)
      if (byteIndex >= cmd.length) {
        throw new Error('parser: command index out of bounds');
      }

      // Use non-null assertion since we've checked the bounds
      const byte = cmd[byteIndex]!;

      if (i % 2 === 0) {
        // First character of a byte (high nibble)
        currentValue = (currentValue << 4) | ((byte >> 4) & 0x0f);
      } else {
        // Second character of a byte (low nibble)
        currentValue = (currentValue << 4) | (byte & 0x0f);
      }

      currentLetter = c;
    } else {
      // This should be a fixed part of the command - verify it matches
      if (c !== s.charAt(i)) {
        throw new Error('parser: command does not match template');
      }
    }
  }

  // Save the last value if there was a parameter
  if (currentLetter !== '') {
    values.push(currentValue);
  }

  return values;
}
